import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { countries, languages, nisabConstants, personTypes, assetFields as baseAssetFields, deductionFields } from '../config/zakatConfig';
import { getCountrySpecificAssets } from '../config/countrySpecificAssets';
import { getTranslation } from '../config/translations';
import { sumAssets, sumDeductions, computeNisab, computeZakat, getMergedPersonFields } from '../utils/zakatCalc';
import { saveZakatSnapshot } from '../utils/zakatAssetBridge';
import { detectCountryCode } from '../utils/detectCountry';

const ZakatGlobalContext = createContext(null);

// API URLs
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const CURRENCY_API_URL = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json';

// Fallback prices per gram in local currency (Feb 2026 estimates)
const fallbackPrices = {
  gold: { USD: 163, GBP: 130, EUR: 150, INR: 13600, AED: 598, SAR: 610, PKR: 45300, MYR: 728, IDR: 2600000, BDT: 19500, TRY: 5950, RUB: 14800, KZT: 81000, UZS: 2050000, KGS: 14200, TJS: 1780, AZN: 277, TMT: 570, NGN: 245000, IRR: 6900000, AFN: 11300, BND: 215, KES: 21100, TZS: 415000, UGX: 615000, ETB: 9500, SOS: 93000, LKR: 49000, PHP: 9150, CNY: 1175, ZAR: 3050, THB: 5550, MMK: 343000, MVR: 2500, CHF: 145, SEK: 1700, DKK: 1120, NOK: 1730, ALL: 15400, BAM: 293, MKD: 9220, RSD: 17600, BGN: 293 },
  silver: { USD: 2.0, GBP: 1.6, EUR: 1.8, INR: 166, AED: 7.3, SAR: 7.5, PKR: 553, MYR: 8.9, IDR: 32000, BDT: 240, TRY: 73, RUB: 181, KZT: 990, UZS: 25000, KGS: 174, TJS: 21.8, AZN: 3.4, TMT: 7.0, NGN: 3000, IRR: 84500, AFN: 139, BND: 2.6, KES: 258, TZS: 5100, UGX: 7520, ETB: 116, SOS: 1140, LKR: 600, PHP: 112, CNY: 14.4, ZAR: 37.5, THB: 68, MMK: 4200, MVR: 30.8, CHF: 1.78, SEK: 20.8, DKK: 13.7, NOK: 21.2, ALL: 189, BAM: 3.6, MKD: 113, RSD: 216, BGN: 3.6 }
};

export const ZakatGlobalProvider = ({ children }) => {
  // Core state
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);
  // Multi-select: an array of person-type ids. The first entry remains the
  // "primary" type for any legacy single-type displays.
  const [selectedPersonTypes, setSelectedPersonTypes] = useState([]);
  const selectedPersonType = selectedPersonTypes[0] || null;
  const setSelectedPersonType = useCallback((id) => {
    setSelectedPersonTypes(id ? [id] : []);
  }, []);
  const togglePersonType = useCallback((id) => {
    setSelectedPersonTypes((prev) => (
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    ));
  }, []);
  const [currentStep, setCurrentStep] = useState(1);
  const [nisabStandard, setNisabStandard] = useState('gold'); // 'gold' or 'silver'
  
  // Live prices
  const [goldPrice, setGoldPrice] = useState(0);
  const [silverPrice, setSilverPrice] = useState(0);
  const [exchangeRates, setExchangeRates] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pricesError, setPricesError] = useState(null);
  
  // Manual rate overrides
  const [useManualRates, setUseManualRates] = useState(false);
  const [manualGoldPrice, setManualGoldPrice] = useState('');
  const [manualSilverPrice, setManualSilverPrice] = useState('');
  
  // Asset values
  const [assetValues, setAssetValues] = useState({});
  const [deductionValues, setDeductionValues] = useState({});

  // Fetch exchange rates
  const fetchExchangeRates = useCallback(async () => {
    try {
      const response = await fetch(CURRENCY_API_URL);
      const data = await response.json();
      if (data && data.usd) {
        setExchangeRates(data.usd);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch exchange rates:', error);
      // Use fallback rates
      setExchangeRates({
        inr: 83.5,
        aed: 3.67,
        sar: 3.75,
        pkr: 278,
        gbp: 0.79,
        eur: 0.92,
        myr: 4.47
      });
    }
  }, []);

  // Metal price source info
  const [priceSource, setPriceSource] = useState('');
  const [priceLastUpdated, setPriceLastUpdated] = useState(null);
  
  // Fetch gold and silver prices from backend
  const fetchMetalPrices = useCallback(async () => {
    setPricesLoading(true);
    setPricesError(null);
    
    try {
      const resp = await fetch(`${BACKEND_URL}/api/metal-prices`);
      if (!resp.ok) throw new Error('API error');
      const data = await resp.json();
      
      const goldPerGramUSD = data.gold_usd_per_gram;
      const silverPerGramUSD = data.silver_usd_per_gram;
      setPriceSource(data.source || 'Market data');
      setPriceLastUpdated(new Date());
      
      // Convert to selected currency
      const currencyCode = selectedCountry.currency.toLowerCase();
      const rate = exchangeRates[currencyCode] || 1;
      
      setGoldPrice(goldPerGramUSD * rate);
      setSilverPrice(silverPerGramUSD * rate);
      
    } catch (error) {
      // Live price API is unreachable. Fall back to a signalled stale
      // rate so the calculator remains usable, but clearly mark the
      // Zakat result as an estimate — per the site's privacy contract we
      // must never present a fabricated live figure as authoritative.
      console.info('Metal prices fallback used:', error?.message || error);
      try {
        const goldPerGramUSD = 2890 / 31.1035;
        const silverPerGramUSD = 32.5 / 31.1035;
        const currencyCode = selectedCountry.currency.toLowerCase();
        const rate = exchangeRates[currencyCode] || 1;
        setGoldPrice(goldPerGramUSD * rate);
        setSilverPrice(silverPerGramUSD * rate);
        setPriceSource('Stale estimate · live prices unavailable');
        setPricesError('Live prices unavailable — figures shown use a cached rate');
      } catch (e2) {
        setPricesError('Failed to load prices');
        const currency = selectedCountry.currency;
        setGoldPrice(fallbackPrices.gold[currency] || fallbackPrices.gold.USD);
        setSilverPrice(fallbackPrices.silver[currency] || fallbackPrices.silver.USD);
        setPriceSource('Offline estimate');
      }
    } finally {
      setPricesLoading(false);
    }
  }, [selectedCountry, exchangeRates]);

  // Auto-detect country + language on mount.
  // Once the user manually picks either, we lock in localStorage and stop auto-detecting on subsequent visits.
  useEffect(() => {
    const STORAGE_KEY = 'zi-locale-v1';

    const inferLangFromCountry = (countryCode) => {
      // Country → preferred display language fallback
      const map = { SA: 'ar', AE: 'ar', PK: 'ur', BD: 'en', IN: 'en', GB: 'en', US: 'en',
                    MY: 'ms', ID: 'ms', FR: 'fr', TR: 'tr',
                    RU: 'ru', KZ: 'ru', UZ: 'ru', KG: 'ru', TJ: 'ru',
                    AZ: 'tr', TM: 'tr', CY: 'tr' };
      const code = map[countryCode];
      return code ? languages.find(l => l.code === code) : null;
    };

    const run = async () => {
      // 0. Honour ?country=AE&lang=ar on first paint (shareable deeplinks)
      let urlCountryCode = null;
      let urlLangCode = null;
      try {
        const params = new URLSearchParams(window.location.search);
        urlCountryCode = (params.get('country') || '').toUpperCase().trim() || null;
        urlLangCode = (params.get('lang') || '').toLowerCase().trim() || null;
      } catch (_e) { /* ignore */ }

      let detectedCountry = null;
      if (urlCountryCode) {
        const c = countries.find((x) => x.code === urlCountryCode);
        if (c) {
          setSelectedCountry(c);
          detectedCountry = c;
        }
      }
      let urlLangApplied = false;
      if (urlLangCode) {
        const l = languages.find((x) => x.code === urlLangCode);
        if (l) {
          setSelectedLanguage(l);
          urlLangApplied = true;
        }
      }
      // If both URL params resolved, we are fully driven by deeplink — done.
      if (detectedCountry && urlLangApplied) return;
      // If a URL lang was supplied but no country, still try to auto-detect country.
      if (urlLangApplied && !detectedCountry) {
        try {
          const code = await detectCountryCode();
          const c = countries.find(cc => cc.code === code);
          if (c) setSelectedCountry(c);
        } catch (_e) { /* ignore */ }
        return;
      }

      // 1. Honour saved user lock
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved && saved.locked) {
          const c = countries.find(x => x.code === saved.country);
          const l = languages.find(x => x.code === saved.language);
          if (c) setSelectedCountry(c);
          if (l) setSelectedLanguage(l);
          return;
        }
      } catch (_e) { /* ignore parse errors */ }

      // 2. Auto-detect country (skip if URL already provided one)
      if (!detectedCountry) {
        try {
          const code = await detectCountryCode();
          detectedCountry = countries.find(c => c.code === code) || null;
          if (detectedCountry) setSelectedCountry(detectedCountry);
        } catch (e) {
          console.info('Country auto-detect fallback:', e?.message || e);
        }
      }

      // 3. Language: default to English on first visit. The LanguagePrompt
      //    component then offers the country's preferred language so the
      //    visitor stays in control.
      const enLang = languages.find(l => l.code === 'en');
      if (enLang) setSelectedLanguage(enLang);
    };

    run();
  }, []);

  // Keep <html dir> in sync with the selected language so RTL works for screen-readers, print and global CSS
  useEffect(() => {
    if (typeof document !== 'undefined' && selectedLanguage) {
      document.documentElement.dir = selectedLanguage.dir || 'ltr';
      document.documentElement.lang = selectedLanguage.code || 'en';
    }
  }, [selectedLanguage]);

  // Fetch prices on mount and when country changes
  useEffect(() => {
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  useEffect(() => {
    if (Object.keys(exchangeRates).length > 0) {
      fetchMetalPrices();
    }
  }, [fetchMetalPrices, exchangeRates, selectedCountry]);

  // Translation helper
  const t = useCallback((path) => {
    return getTranslation(selectedLanguage.code, path);
  }, [selectedLanguage]);

  // Format currency
  const formatCurrency = useCallback((value) => {
    const symbol = selectedCountry.currencySymbol || selectedCountry.currency;
    if (typeof value !== 'number' || isNaN(value)) return `${symbol}0`;

    // Format as a plain decimal then prepend the configured currencySymbol.
    // We previously delegated to Intl.NumberFormat with style:'currency', but
    // that swaps in the ISO code (NOK, BGN, MMK, BAM, RSD, MKD…) instead of
    // honouring our `currencySymbol` field (kr, лв, K, KM, дин, ден…) for
    // currencies that don't have a CLDR symbol on the user's locale.
    const formatted = new Intl.NumberFormat(
      selectedLanguage.code === 'ar' ? 'ar-SA' : 'en-US',
      { minimumFractionDigits: 0, maximumFractionDigits: 0 },
    ).format(value);
    return `${symbol}${formatted}`;
  }, [selectedCountry, selectedLanguage]);

  // Persist user's manual choice (locks auto-detect for subsequent visits)
  // `languageExplicit` is set only when the user explicitly picked a LANGUAGE
  // via the language switcher — used by LanguagePrompt to decide whether to
  // offer a local-language switch.
  const persistLocaleLock = useCallback((country, language, languageExplicit = false) => {
    try {
      // Preserve previously-set languageExplicit if not overriding now
      const prev = JSON.parse(localStorage.getItem('zi-locale-v1') || 'null') || {};
      localStorage.setItem('zi-locale-v1', JSON.stringify({
        locked: true,
        country: country?.code,
        language: language?.code,
        languageExplicit: languageExplicit || !!prev.languageExplicit,
      }));
    } catch (_e) { /* localStorage unavailable */ }
  }, []);

  // Update country
  const updateCountry = useCallback((countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setSelectedCountry(country);
      persistLocaleLock(country, selectedLanguage, false);
    }
  }, [persistLocaleLock, selectedLanguage]);

  // Update language
  const updateLanguage = useCallback((langCode) => {
    const lang = languages.find(l => l.code === langCode);
    if (lang) {
      setSelectedLanguage(lang);
      persistLocaleLock(selectedCountry, lang, true);
    }
  }, [persistLocaleLock, selectedCountry]);

  // Update asset value
  const updateAssetValue = useCallback((field, value) => {
    setAssetValues(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  // Update deduction value
  const updateDeductionValue = useCallback((field, value) => {
    setDeductionValues(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  // Effective prices (respects manual overrides)
  const effectiveGoldPrice = useManualRates && parseFloat(manualGoldPrice) > 0 ? parseFloat(manualGoldPrice) : goldPrice;
  const effectiveSilverPrice = useManualRates && parseFloat(manualSilverPrice) > 0 ? parseFloat(manualSilverPrice) : silverPrice;

  // Persist a snapshot of Zakat assets so Inheritance & Will pages can
  // offer to import them. Debounced via the React render cycle.
  // Skip until the user has entered values so a fresh page load never wipes the saved snapshot.
  const snapshotArmed = React.useRef(false);
  useEffect(() => {
    if (!snapshotArmed.current && Object.keys(assetValues).length === 0) return;
    snapshotArmed.current = true;
    saveZakatSnapshot({
      assetValues,
      goldPricePerGram: effectiveGoldPrice,
      silverPricePerGram: effectiveSilverPrice,
      currency: selectedCountry?.currency,
      country: selectedCountry?.code,
    });
  }, [assetValues, effectiveGoldPrice, effectiveSilverPrice, selectedCountry]);

  // Rate variation (manual vs live)
  const goldVariation = goldPrice > 0 && useManualRates && parseFloat(manualGoldPrice) > 0
    ? ((parseFloat(manualGoldPrice) - goldPrice) / goldPrice * 100) : 0;
  const silverVariation = silverPrice > 0 && useManualRates && parseFloat(manualSilverPrice) > 0
    ? ((parseFloat(manualSilverPrice) - silverPrice) / silverPrice * 100) : 0;

  // Calculate total assets
  const calculateTotalAssets = useCallback(
    () => sumAssets(assetValues, effectiveGoldPrice, effectiveSilverPrice),
    [assetValues, effectiveGoldPrice, effectiveSilverPrice]
  );

  // Calculate total deductions
  const calculateTotalDeductions = useCallback(
    () => sumDeductions(deductionValues),
    [deductionValues]
  );

  // Calculate nisab threshold
  const calculateNisab = useCallback(
    () => computeNisab(nisabStandard, effectiveGoldPrice, effectiveSilverPrice),
    [nisabStandard, effectiveGoldPrice, effectiveSilverPrice]
  );

  // Calculate zakat
  const calculateZakat = useCallback(
    () =>
      computeZakat({
        assetValues,
        deductionValues,
        nisabStandard,
        goldPricePerGram: effectiveGoldPrice,
        silverPricePerGram: effectiveSilverPrice,
        liveGoldPrice: goldPrice,
        liveSilverPrice: silverPrice,
        currency: selectedCountry.currency,
      }),
    [
      assetValues,
      deductionValues,
      nisabStandard,
      effectiveGoldPrice,
      effectiveSilverPrice,
      goldPrice,
      silverPrice,
      selectedCountry,
    ]
  );

  // Reset calculator
  const resetCalculator = useCallback(() => {
    setAssetValues({});
    setDeductionValues({});
    setSelectedPersonTypes([]);
    setCurrentStep(1);
  }, []);

  // Country-specific scheme fields (India→PPF/EPF/NPS, UK→ISA/SIPP, etc.)
  // merged into the base asset-field catalogue when a matching country is
  // active. Falls back gracefully when the country has no curated schemes.
  const assetFields = useMemo(
    () => ({ ...baseAssetFields, ...getCountrySpecificAssets(selectedCountry?.code) }),
    [selectedCountry?.code]
  );

  // Get available fields for selected person type(s) — merged & deduped
  const getAvailableAssetFields = useCallback(
    () => getMergedPersonFields(selectedPersonTypes, 'assetFields'),
    [selectedPersonTypes]
  );

  const getAvailableDeductionFields = useCallback(
    () => getMergedPersonFields(selectedPersonTypes, 'deductionFields'),
    [selectedPersonTypes]
  );

  const value = {
    // State
    selectedCountry,
    selectedLanguage,
    selectedPersonType,
    selectedPersonTypes,
    currentStep,
    nisabStandard,
    goldPrice,
    silverPrice,
    effectiveGoldPrice,
    effectiveSilverPrice,
    exchangeRates,
    pricesLoading,
    pricesError,
    priceSource,
    priceLastUpdated,
    assetValues,
    deductionValues,
    useManualRates,
    manualGoldPrice,
    manualSilverPrice,
    goldVariation,
    silverVariation,
    
    // Config
    countries,
    languages,
    personTypes,
    assetFields,
    countrySpecificAssetFields: getCountrySpecificAssets(selectedCountry?.code),
    deductionFields,
    nisabConstants,
    
    // Actions
    updateCountry,
    updateLanguage,
    setSelectedPersonType,
    togglePersonType,
    setCurrentStep,
    setNisabStandard,
    updateAssetValue,
    updateDeductionValue,
    resetCalculator,
    fetchMetalPrices,
    setUseManualRates,
    setManualGoldPrice,
    setManualSilverPrice,
    
    // Calculations
    calculateTotalAssets,
    calculateTotalDeductions,
    calculateNisab,
    calculateZakat,
    getAvailableAssetFields,
    getAvailableDeductionFields,
    
    // Helpers
    t,
    formatCurrency
  };

  return (
    <ZakatGlobalContext.Provider value={value}>
      {children}
    </ZakatGlobalContext.Provider>
  );
};

export const useZakatGlobal = () => {
  const context = useContext(ZakatGlobalContext);
  if (!context) {
    throw new Error('useZakatGlobal must be used within a ZakatGlobalProvider');
  }
  return context;
};

export default ZakatGlobalContext;
